// 所要量展開 (BOM explosion) panel — tree + leaves cost table.
import { TreeStructure, Wrench } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { yen, flattenTree } from './bomUtils';

function TreeRow({ node, depth }) {
  const short = (node.required || 0) - (node.on_hand || 0) > 1e-6;
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-zinc-50 text-sm" style={{ paddingLeft: `${depth * 18}px` }} data-testid={`bom-tree-${node.item_id}`}>
      <TreeStructure size={14} className={depth === 0 ? 'text-orange-500' : 'text-zinc-300'} />
      <span className="font-medium text-zinc-800">{node.name || '—'}</span>
      {node.sku && <span className="font-mono text-[11px] text-zinc-400">{node.sku}</span>}
      <span className="ml-auto text-xs text-zinc-500 tabular-nums">所要 <b className="text-zinc-800">{node.required}</b> {node.uom || ''}</span>
      <span className={`text-xs tabular-nums ${short ? 'text-rose-600 font-semibold' : 'text-emerald-600'}`}>在庫 {node.on_hand}</span>
    </div>
  );
}

export const BomExplosion = ({ explosion, explodeQty, onQtyChange }) => {
  const { t } = useT();
  return (
    <section className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
        <h2 className="text-sm font-semibold text-zinc-800">{t('mfg.bom.explosion')}</h2>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-zinc-500">{t('mfg.bom.explodeQty')}</Label>
          <Input type="number" data-testid="bom-explode-qty" value={explodeQty} onChange={(e) => onQtyChange(e.target.value)} className="h-8 w-20 text-right text-xs" />
        </div>
      </div>
      <div className="p-4">
        {explosion?.tree ? flattenTree(explosion.tree, 0, []).map(({ node, depth }, i) => <TreeRow key={`${node.item_id}-${depth}-${i}`} node={node} depth={depth} />) : <p className="text-center text-sm text-zinc-400 py-6">—</p>}
        {explosion?.leaves?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-zinc-500 mb-1.5">{t('mfg.bom.leaves')}</p>
            <table className="w-full text-xs" data-testid="bom-leaves-table">
              <thead><tr className="text-zinc-400 border-b border-zinc-200"><th className="text-left py-1">{t('mfg.bom.component')}</th><th className="text-right py-1">{t('mfg.bom.required')}</th><th className="text-right py-1">{t('mfg.bom.onHand')}</th><th className="text-right py-1">{t('mfg.bom.shortage')}</th><th className="text-right py-1">{t('mfg.bom.unitCost')}</th><th className="text-right py-1">{t('mfg.bom.lineCost')}</th></tr></thead>
              <tbody>{explosion.leaves.map((l) => (
                <tr key={l.item_id} className="border-b border-zinc-50">
                  <td className="py-1 text-zinc-700">{l.name}{l.is_subcontracted && <span className="ml-1 inline-flex items-center text-orange-600" title={t('mfg.processing.subcontracted')}><Wrench size={11} weight="fill" /></span>}</td>
                  <td className="py-1 text-right tabular-nums">{l.required}</td>
                  <td className="py-1 text-right tabular-nums">{l.on_hand}</td>
                  <td className="py-1 text-right tabular-nums">{l.shortage > 0 ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{l.shortage}</Badge> : <span className="text-emerald-600">0</span>}</td>
                  <td className="py-1 text-right tabular-nums text-zinc-500">{yen(l.unit_cost)}</td>
                  <td className="py-1 text-right tabular-nums">{yen(l.line_cost)}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr className="border-t border-zinc-200 font-semibold"><td className="py-1.5 text-zinc-700" colSpan={5}>{t('mfg.bom.totalCost')}</td><td className="py-1.5 text-right tabular-nums text-orange-600" data-testid="bom-total-cost">{yen(explosion.total_cost)}</td></tr></tfoot>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
